import { useMemo } from 'react';
import styled from 'styled-components';
import { Tooltip } from '@mui/material';
import Chip from '../../Chip';
import { AgentContext } from '../../../types';
import useCachedEnvironments from '../../../hooks/useCachedEnvironments';
import useSelectedEnv from '../../../hooks/useSelectedEnv';
import { pluralizeString } from '../../../utils/common';

const InlineWrapper = styled.span`
  display: inline;
  margin-right: 4px;
`;

const StyledChip = styled(Chip)`
  font-size: 12px;
  height: 21px;
  color: ${({ theme }) => theme.palette.common.black};
  margin-right: 4px;
`;

type Props = {
  context: AgentContext;
};

const MessageContextChips = ({ context }: Props) => {
  const { data: environments } = useCachedEnvironments();
  const selectedEnvId = useSelectedEnv();

  const envIdToName = useMemo(() => {
    const map = new Map<string, string>();
    (environments ?? []).forEach((env) => map.set(env.id, env.name));
    return map;
  }, [environments]);

  const chips = useMemo(() => {
    const result: { label: string; ids: string[] }[] = [];
    // Hide the env chip when the message's only env matches the current page
    // env — it adds no information beyond the already-selected environment.
    const isCurrentEnv =
      context.environmentIds?.length === 1 &&
      context.environmentIds[0] === selectedEnvId;
    if (context.environmentIds?.length && !isCurrentEnv) {
      const envNames = context.environmentIds.map(
        (id) => envIdToName.get(id) ?? id,
      );
      result.push({
        label:
          context.environmentIds.length === 1
            ? envNames[0]
            : pluralizeString(
                context.environmentIds.length,
                'environment',
                'environments',
              ),
        ids: envNames,
      });
    }
    if (context.testIds?.length) {
      result.push({
        label: pluralizeString(context.testIds.length, 'test', 'tests'),
        ids: context.testIds,
      });
    }
    if (context.assetIds?.length) {
      result.push({
        label: pluralizeString(context.assetIds.length, 'asset', 'assets'),
        ids: context.assetIds,
      });
    }
    if (context.incidentIds?.length) {
      result.push({
        label: pluralizeString(
          context.incidentIds.length,
          'incident',
          'incidents',
        ),
        ids: context.incidentIds,
      });
    }
    return result;
  }, [context, envIdToName, selectedEnvId]);

  if (chips.length === 0) return null;

  return (
    <InlineWrapper>
      {chips.map((chip) => (
        <Tooltip
          key={chip.label}
          title={
            <div>
              {(context.displayLabels ?? chip.ids).map((value) => (
                <div key={value}>{value}</div>
              ))}
            </div>
          }
        >
          <span>
            <StyledChip label={chip.label} />
          </span>
        </Tooltip>
      ))}
    </InlineWrapper>
  );
};

export default MessageContextChips;
